/**
 * ThreadListItem — Row in the messaging inbox.
 * Shows entity icon, name, last message preview, timeAgo, and unread badge.
 */

import React from "react";
import { Badge, Typography, theme } from "antd";
import { timeAgo } from "@/utils/timeAgo";
import type { ThreadSummary } from "@/hooks/useMessageInbox";

const { Text } = Typography;

interface ThreadListItemProps {
  thread: ThreadSummary;
  selected?: boolean;
  onClick?: () => void;
}

export const ThreadListItem: React.FC<ThreadListItemProps> = ({
  thread,
  selected,
  onClick,
}) => {
  const {
    token: {
      colorBgTextHover,
      colorPrimaryBg,
      colorText,
      fontSizeSM,
    },
  } = theme.useToken();

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        cursor: "pointer",
        background: selected ? colorPrimaryBg : "transparent",
        borderLeft: selected ? "3px solid #6366f1" : "3px solid transparent",
        transition: "all 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (!selected)
          (e.currentTarget as HTMLDivElement).style.background =
            colorBgTextHover;
      }}
      onMouseLeave={(e) => {
        if (!selected)
          (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      {/* Unread dot */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: thread.unreadCount > 0 ? "#6366f1" : "transparent",
          flexShrink: 0,
          marginTop: 6,
        }}
      />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Entity name + time */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <Text
            strong={thread.unreadCount > 0}
            style={{
              fontSize: 13,
              color: colorText,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {thread.entityName}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: fontSizeSM - 1, flexShrink: 0 }}
          >
            {timeAgo(thread.lastTime)}
          </Text>
        </div>

        {/* Last message preview */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
          }}
        >
          <Text
            type="secondary"
            style={{
              fontSize: fontSizeSM,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              fontWeight: thread.unreadCount > 0 ? 500 : 400,
            }}
          >
            {thread.lastAuthor && (
              <span style={{ fontWeight: 500 }}>{thread.lastAuthor}: </span>
            )}
            {thread.lastMessage.replace(/@\[[^\]]+\]\([^)]+\)/g, (m) => {
              const name = m.match(/@\[([^\]]+)\]/);
              return name ? `@${name[1]}` : m;
            })}
          </Text>
          {thread.unreadCount > 0 && (
            <Badge
              count={thread.unreadCount}
              size="small"
              style={{ backgroundColor: "#6366f1" }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
